import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import { ModalTransitions } from "molecules/constants/modal-transitions";
import { ModalTypes } from "molecules/constants/modal-types";
import Modal from "molecules/modals/modal";
import BreadcrumbNavigation from "organisms/breadcrumbs/breadcrumb-navigation";
import BreadcrumbsModal from "organisms/breadcrumbs/breadcrumbs-modal";
import ChangeIndicatorLegend from "organisms/change-indicator-legend/change-indicator-legend";
import BookviewTabPanel from "organisms/panels/bookview-tab-panels/bookview-tab-panel";
import MobileTableOfContentsButton from "organisms/table-of-contents/mobile-table-of-contents-button";
import useToggleNotebookSidebar from "pages/notebooks/use-toggle-notebook-sidebar";
import React, { PropsWithChildren } from "react";
import { Options } from "use-react-router-breadcrumbs";
import useMetaNav from "utilities/atoms/metanav-visible-atom";
import { BookviewSearchProvider } from "utilities/contexts/bookview-tab-panel/bookview-search-provider";
import {
    EventsProvider,
    SearchSource,
} from "utilities/contexts/events/events-provider";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useNotebookBreakpoints from "utilities/hooks/domain/notebooks/use-notebook-breakpoints";
import useIsNestedRoute from "utilities/hooks/routing/use-is-nested-route";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import { useLocalization } from "utilities/hooks/use-localization";
import useModalActions from "utilities/hooks/use-modal-actions";
import { CULTURE_PARAM } from "utilities/route-utils";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

export interface BreadcrumbsMenuProps extends PropsWithChildren {
    fullWidth?: boolean;
    showOverflowMenu?: boolean;
    hideBookviewPanelOptions?: boolean;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

export const BREADCRUMB_OPTIONS: Options = {
    // Disable default behavior of making API calls based on route
    disableDefaults: true,
    // Publication path will match twice unless excluded
    excludePaths: [`${CULTURE_PARAM}/publications/:id`],
};

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const BreadcrumbsMenu: React.FC<BreadcrumbsMenuProps> = (
    props: BreadcrumbsMenuProps
) => {
    const cssBaseClassName = "c-breadcrumbs-menu";
    const { isOpen, handleClose } = useModalActions();
    const { toggleDisplay } = useToggleNotebookSidebar();

    const { t } = useLocalization();
    const openNavigation = t("openNavigation");
    const fullWidthModifier = props.fullWidth === true ? "-full-width" : "";

    const { routeIsBookView, routeIsNotebooks } = useIsNestedRoute();

    const { toggle } = useMetaNav();

    const isTablet = useBreakpoint(
        Breakpoints.Tablet,
        BreakpointComparer.MaxWidth
    );

    const { isMobile: isSmTablet } = useNotebookBreakpoints();

    const { inMobileView } = useBreakpointRanges();

    const mobileRouteComponent = (() => {
        if (routeIsBookView) return "bookview";
        if (routeIsNotebooks) return "notebooks";
        return "default";
    })();

    const MobileRouteComponents = {
        bookview: (
            <MobileTableOfContentsButton
                cssClassName={`${cssBaseClassName}__metapanel-trigger`}
            />
        ),
        notebooks: isSmTablet && (
            <div className={`${cssBaseClassName}__metapanel-trigger`}>
                <button
                    type="button"
                    name={"openNotebook"}
                    onClick={toggleDisplay}>
                    <Icon type={Icons.Notebook} size={IconSizes.Large} />
                </button>
            </div>
        ),
        default: (
            <div className={`${cssBaseClassName}__metapanel-trigger`}>
                <button type="button" name={openNavigation} onClick={toggle}>
                    <Icon type={Icons.MetaPanel} size={IconSizes.Large} />
                </button>
            </div>
        ),
    };

    return (
        <div className={`${cssBaseClassName} ${fullWidthModifier}`}>
            {isTablet && MobileRouteComponents[mobileRouteComponent]}
            <BreadcrumbNavigation options={BREADCRUMB_OPTIONS} />
            {routeIsBookView && !props.hideBookviewPanelOptions && (
                <div className={`${cssBaseClassName}__controls`}>
                    {!inMobileView && <ChangeIndicatorLegend />}
                    <EventsProvider source={SearchSource.Bookview}>
                        <BookviewSearchProvider>
                            <BookviewTabPanel />
                        </BookviewSearchProvider>
                    </EventsProvider>
                </div>
            )}
            {props.children && (
                <div className={`${cssBaseClassName}__controls`}>
                    {props.children}
                </div>
            )}
            <Modal
                closeDialog={handleClose}
                isVisible={isOpen}
                label={t("breadcrumbs")}
                transition={ModalTransitions.SlideUp}
                type={ModalTypes.Bottom}>
                <BreadcrumbsModal
                    onBreadcrumbClick={handleClose}
                    options={BREADCRUMB_OPTIONS}
                />
            </Modal>
        </div>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default BreadcrumbsMenu;

// #endregion Exports
